/**
 * Shared HD Steth "virtual audio device".
 *
 * The HD Steth is a BLE electronic stethoscope bridged to the browser by the
 * local medconnect-devices agent, which re-emits decoded sample batches over a
 * socket.io gateway (port 4000). It is NOT an OS audio input, so it can't be
 * discovered via navigator.mediaDevices — instead we synthesize a real
 * MediaStream from the heart-sound channel (socket -> AudioWorklet ->
 * MediaStreamAudioDestinationNode) so it can be selected and published through
 * the exact same RTC pipeline as any microphone.
 *
 * A single module-level manager owns one socket + one audio bridge, shared
 * across every consumer via a subscribe/snapshot pattern. The device is only
 * reported `available` while batches are actually arriving, so it appears in
 * the device picker exactly when the physical stethoscope is streaming.
 *
 * Consumers must never stop the shared stream's tracks (the RTC services and
 * the picker preview all call `track.stop()` on teardown), so each gets its
 * own destination-node stream via `createStream()`. Chrome delivers silence
 * into MediaStream.clone() of a WebAudio destination track, so cloning is
 * not an option; a dedicated destination node per consumer is.
 */

import { useEffect, useState } from 'react'
import { io, Socket } from 'socket.io-client'

import type { DiagnosticRTCCustomDevice } from '@/providers/diagnosticRTC/types'
import { HDStethAudioBridge } from '@/utils/hdSteth/audioBridge'
import { HDSTETH_SAMPLE_RATE_HZ, HDStethBatch, isHDStethBatch } from '@/utils/hdSteth/types'

/** Sentinel deviceId used across the RTC services to recognise the HD Steth. */
export const HDSTETH_DEVICE_ID = 'hdsteth'
export const HDSTETH_DEVICE_LABEL = 'HD Steth'

/**
 * Picker entry injected into the `stethoscopes` list of every RTC service
 * while the device is streaming. Shaped like a configured custom stethoscope
 * so the Stethoscope component and DeviceSelectionDialog need no changes.
 */
export const HDSTETH_RTC_DEVICE: DiagnosticRTCCustomDevice = {
  id: HDSTETH_DEVICE_ID,
  device: { deviceId: HDSTETH_DEVICE_ID, label: HDSTETH_DEVICE_LABEL, kind: 'audioinput' },
  customName: HDSTETH_DEVICE_LABEL,
  iconURL: '',
  type: 'stethoscope',
}

export const isHDStethDeviceId = (deviceId?: string | null): boolean => deviceId === HDSTETH_DEVICE_ID

const STREAM_URL = 'http://localhost:4000'
/** Heart-sound sample rate of the device; `measuredHsRateHz` keeps reporting the live value. */
const HS_SAMPLE_RATE_HZ = HDSTETH_SAMPLE_RATE_HZ
/** Consider the device gone if no batch arrives within this window. */
const STALE_MS = 2000
/** Window over which the inbound HS sample rate is measured and logged. */
const RATE_WINDOW_MS = 5000

export interface HDStethDeviceState {
  /** True while the physical stethoscope is connected and streaming samples. */
  available: boolean
  /** Shared synthetic heart-sound MediaStream. Do not stop its tracks; prefer `createStream()`. */
  stream: MediaStream | null
  /** Inbound heart-sound sample rate measured over the last window, or null before the first window. */
  measuredHsRateHz: number | null
  /** Operating mode reported by the agent ('6-bit' | '12-bit'), or null until the first batch. */
  mode: HDStethBatch['mode'] | null
  /** Dedicated per-consumer output stream, safe to stop. Null while the bridge isn't ready. */
  createStream: () => MediaStream | null
  /** Resume the bridge's AudioContext (call from a user gesture). */
  resume: () => Promise<void>
}

class HDStethDeviceManager {
  private socket: Socket | null = null
  private bridge: HDStethAudioBridge | null = null
  private stream: MediaStream | null = null
  private available = false
  private measuredHsRateHz: number | null = null
  private mode: HDStethBatch['mode'] | null = null
  private lastBatchAt = 0
  private rateWindowStart = 0
  private rateWindowSamples = 0
  private refCount = 0
  private staleTimer: ReturnType<typeof setInterval> | null = null
  private listeners = new Set<() => void>()
  private batchListeners = new Set<(batch: HDStethBatch | null) => void>()
  /** Stable across snapshots so consumers can list them in dependency arrays safely. */
  private readonly createStreamBound = (): MediaStream | null => this.createStream()
  private readonly resumeBound = (): Promise<void> => this.resume()
  private snapshot: HDStethDeviceState = this.buildSnapshot()

  getSnapshot(): HDStethDeviceState {
    return this.snapshot
  }

  subscribeBatches(listener: (batch: HDStethBatch | null) => void): () => void {
    this.batchListeners.add(listener)
    const unsubscribe = this.subscribe(() => undefined)
    return () => { this.batchListeners.delete(listener); unsubscribe() }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    this.refCount += 1
    if (this.refCount === 1) this.startConnection()
    return () => {
      this.listeners.delete(listener)
      this.refCount -= 1
      if (this.refCount === 0) this.stopConnection()
    }
  }

  async resume(): Promise<void> {
    await this.bridge?.resume()
  }

  createStream(): MediaStream | null {
    return this.bridge?.createOutputStream() ?? null
  }

  private buildSnapshot(): HDStethDeviceState {
    return {
      available: this.available,
      stream: this.stream,
      measuredHsRateHz: this.measuredHsRateHz,
      mode: this.mode,
      createStream: this.createStreamBound,
      resume: this.resumeBound,
    }
  }

  private refreshSnapshot(): void {
    this.snapshot = this.buildSnapshot()
    this.listeners.forEach((l) => l())
  }

  private async startConnection(): Promise<void> {
    const bridge = new HDStethAudioBridge()
    bridge.setSourceRate(HS_SAMPLE_RATE_HZ)
    this.bridge = bridge
    let stream: MediaStream | null = null
    try {
      stream = await bridge.start()
    } catch (error) {
      console.error('🩺 HD Steth audio bridge failed to start', error)
    }
    // A stopConnection() that ran while we awaited has superseded this start.
    if (this.bridge !== bridge) {
      bridge.stop()
      return
    }
    this.stream = stream
    this.refreshSnapshot()

    const socket = io(STREAM_URL, { transports: ['websocket', 'polling'] })
    this.socket = socket

    socket.on('hdsteth-batch', (batch: unknown) => {
      if (isHDStethBatch(batch) && Math.abs(Date.now() - batch.ts) < STALE_MS) this.handleBatch(batch)
    })
    socket.on('disconnect', () => this.markUnavailable())

    this.staleTimer = setInterval(() => {
      if (this.available && Date.now() - this.lastBatchAt > STALE_MS) this.markUnavailable()
    }, 1000)
  }

  private handleBatch(batch: HDStethBatch): void {
    const now = Date.now()
    this.lastBatchAt = now
    this.bridge?.pushBatch(batch)
    this.measureRate(now, batch.hs.length)

    let changed = false
    if (!this.available) {
      this.available = true
      changed = true
    }
    if (batch.mode !== this.mode) {
      this.mode = batch.mode
      changed = true
    }
    if (changed) this.refreshSnapshot()
    this.batchListeners.forEach((listener) => listener(batch))
  }

  /** Count HS samples per fixed window and expose/log the resulting rate. */
  private measureRate(now: number, sampleCount: number): void {
    if (this.rateWindowStart === 0) {
      this.rateWindowStart = now
      this.rateWindowSamples = 0
      return
    }
    this.rateWindowSamples += sampleCount
    const elapsed = now - this.rateWindowStart
    if (elapsed < RATE_WINDOW_MS) return

    this.measuredHsRateHz = Math.round((this.rateWindowSamples * 1000) / elapsed)
    this.rateWindowStart = now
    this.rateWindowSamples = 0
    console.info(`🩺 HD Steth inbound HS rate ≈ ${this.measuredHsRateHz} Hz (bridge assumes ${HS_SAMPLE_RATE_HZ} Hz, mode ${this.mode})`)
    this.refreshSnapshot()
  }

  private markUnavailable(): void {
    this.batchListeners.forEach((listener) => listener(null))
    this.rateWindowStart = 0
    this.rateWindowSamples = 0
    if (this.available) {
      this.available = false
      this.refreshSnapshot()
    }
  }

  private stopConnection(): void {
    if (this.staleTimer) {
      clearInterval(this.staleTimer)
      this.staleTimer = null
    }
    if (this.socket) {
      this.socket.off('hdsteth-batch')
      this.socket.close()
      this.socket = null
    }
    this.bridge?.stop()
    this.bridge = null
    this.stream = null
    this.available = false
    this.measuredHsRateHz = null
    this.mode = null
    this.rateWindowStart = 0
    this.rateWindowSamples = 0
    this.refreshSnapshot()
  }
}

const manager = new HDStethDeviceManager()

/** Subscribe to the existing connector socket; null marks a disconnect or stale device. */
export function subscribeHDStethBatches(listener: (batch: HDStethBatch | null) => void): () => void {
  return manager.subscribeBatches(listener)
}

/**
 * Subscribe to the shared HD Steth virtual device. Pass `enabled = false`
 * (e.g. for non-audio contexts) to opt out of the socket connection.
 */
export function useHDStethDevice(enabled = true): HDStethDeviceState {
  const [state, setState] = useState<HDStethDeviceState>(() => manager.getSnapshot())

  useEffect(() => {
    if (!enabled) return
    const unsubscribe = manager.subscribe(() => setState(manager.getSnapshot()))
    setState(manager.getSnapshot())
    return unsubscribe
  }, [enabled])

  return state
}
